import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DeviceFrame } from '../shell/DeviceFrame';
import { ChallanListingScreen } from '../screens/ChallanListingScreen';
import { Button } from '../design-system/Button';
import { Text } from '../design-system/Text';
import { ReviewModePanel } from './ReviewModePanel';
import { usePrototypeStore } from '../../stores/prototypeStore';
import { useSessionStore } from '../../stores/sessionStore';
import { analytics } from '../../lib/analytics';
import { RotateCcw, GitCompare, Play, ExternalLink } from 'lucide-react';

export function PrototypeHome() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [linkUrl, setLinkUrl] = useState('');

  const activeVariant = usePrototypeStore((s) => s.activeVariant);
  const qualificationState = usePrototypeStore((s) => s.qualificationState);
  const balanceValue = usePrototypeStore((s) => s.balanceValue);
  const listLength = usePrototypeStore((s) => s.listLength);
  const deviceFrame = usePrototypeStore((s) => s.deviceFrame);
  const theme = usePrototypeStore((s) => s.theme);
  const writeToQuery = usePrototypeStore((s) => s.writeToQuery);
  const resetPrototype = usePrototypeStore((s) => s.reset);
  const resetSession = useSessionStore((s) => s.resetAll);

  // Hydrate from URL on mount
  useEffect(() => {
    usePrototypeStore.getState().hydrateFromQuery(searchParams);
  }, []);

  // Sync URL and deep-link preview
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    writeToQuery(params);
    setSearchParams(params, { replace: true });
    setLinkUrl(`${window.location.origin}/challan-listing?${params.toString()}`);
  }, [
    activeVariant,
    qualificationState,
    balanceValue,
    listLength,
    deviceFrame,
    theme,
  ]);

  useEffect(() => {
    analytics.emit('review_mode_open');
  }, []);

  const openRecommended = () => {
    analytics.emit('open_recommended_default');
    const params = new URLSearchParams(searchParams);
    params.set('variant', 'recommended');
    params.set('state', 'negative');
    params.set('list', 'long');
    navigate(`/challan-listing?${params.toString()}`);
  };

  const compare = () => {
    analytics.emit('compare_open');
    navigate('/compare');
  };

  const demo = () => {
    analytics.emit('demo_open');
    navigate('/demo');
  };

  const reset = () => {
    resetPrototype();
    resetSession();
    analytics.emit('session_reset');
  };

  return (
    <div className="min-h-screen bg-slate-100 p-6 dark:bg-slate-950">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6 flex items-center justify-between">
          <div>
            <Text variant="display-medium">Park+ Prototype Review</Text>
            <Text variant="body" color="secondary">
              Challan Listing + FASTag nudge variants
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={compare}>
              <GitCompare className="h-4 w-4" />
              Compare
            </Button>
            <Button variant="outline" onClick={demo}>
              <Play className="h-4 w-4" />
              Demo
            </Button>
            <Button variant="ghost" onClick={reset}>
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
          <ReviewModePanel />

          <div className="flex flex-col items-center gap-6">
            <DeviceFrame device={deviceFrame} theme={theme}>
              <ChallanListingScreen />
            </DeviceFrame>

            <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
              <Text variant="label">Deep link preview</Text>
              <input
                readOnly
                value={linkUrl}
                className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300"
                onFocus={(e) => e.target.select()}
              />
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={openRecommended} aria-label="Open recommended default">
                  Open recommended
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate('/challan-listing')}>
                  <ExternalLink className="h-4 w-4" />
                  Open current
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
